import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { debugLogger } from '@/utils/debugLogger';
import { showSuccessToast, showErrorToast, showWarningToast } from '@/utils/toastUtils';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import SearchAndFilters from './SearchAndFilters';
import UsersList from './UsersList';
import UserActionsModal from './UserActionsModal';
import { supabase } from '@/integrations/supabase/client';

import AdminFiltersModal from './AdminFiltersModal';
import RoleManagementDialog from './RoleManagementDialog';
import { useUserSorting, SortOption } from '@/hooks/useUserSorting';
import { AdminUserFilters } from '@/hooks/useAdminFilters';
import { UserRoleRules } from '@/core/BusinessRulesEngine/rules/UserRoleRules';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle } from 'lucide-react';

interface AdminUserManagementProps {
  users: any[];
  loading: boolean;
  refetch: () => Promise<void>;
  loadMoreUsers: () => Promise<void>;
  hasMoreUsers: boolean;
  loadingMore: boolean;
  totalUsers?: number; // Total de usuários no filtro atual
  // Filter props - passed from parent to ensure single source of truth
  filters: AdminUserFilters;
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  updateFilter: <K extends keyof AdminUserFilters>(key: K, value: AdminUserFilters[K]) => void;
  clearFilters: () => void;
  getActiveFiltersCount: () => number;
}

const AdminUserManagement: React.FC<AdminUserManagementProps> = ({ 
  users, 
  loading, 
  refetch, 
  loadMoreUsers, 
  hasMoreUsers, 
  loadingMore,
  totalUsers,
  filters,
  filterOpen,
  setFilterOpen,
  updateFilter,
  clearFilters,
  getActiveFiltersCount
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [sortField, setSortField] = useState<SortOption>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [planTypeFilter, setPlanTypeFilter] = useState<'all' | 'free' | 'standard' | 'premium'>('all');
  
  // Search term now comes from filters (database-level filtering)
  const searchTerm = filters.searchTerm || '';
  const setSearchTerm = (term: string) => updateFilter('searchTerm', term || undefined);
  
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const { isAdmin: currentUserIsAdmin } = useIsAdmin();
  
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState<any | null>(null);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any | null>(null);

  // Get current user ID
  React.useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setCurrentUserId(user.id);
    };
    getCurrentUser();
  }, []);

  // All filtering is now done at database level - just apply basic plan filter for UI tabs
  const filteredUsers = useMemo(() => {
    // Only apply planTypeFilter locally since it's a quick UI toggle
    if (planTypeFilter === 'all') return users;
    return users.filter(user => user.plan_type === planTypeFilter);
  }, [users, planTypeFilter]);

  // Apply sorting
  const sortedUsers = useUserSorting(filteredUsers, { field: sortField, direction: sortDirection });

  const handleViewProfile = (username: string) => {
    navigate(`/main/user/${username}`);
  };

  const handleManageRole = (user: any) => {
    setSelectedUser(user);
    setRoleModalOpen(true);
  };

  const handleUserAction = async (action: string, userId: string) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;

      switch (action) {
        case 'delete-permanently':
          setUserToDelete(user);
          setDeleteDialogOpen(true);
          return;

        case 'toggle-visibility':
          // Validar via BRE antes de alterar visibilidade
          const validation = await UserRoleRules.canToggleUserVisibility(
            { id: currentUserId } as any,
            { id: userId } as any
          );

          if (!validation.allowed) {
            toast({
              title: 'Ação Negada',
              description: validation.reason,
              variant: 'destructive'
            });
            return;
          }

          const { error: visibilityError } = await supabase
            .from('profiles')
            .update({ hidden: !user?.hidden })
            .eq('id', userId);
          
          if (visibilityError) throw visibilityError;
          toast({
            title: "Visibilidade alterada",
            description: `Perfil ${user?.hidden ? 'visível' : 'oculto'}`,
          });
          break;

        case 'reset-onboarding':
          const { error: onboardingError } = await supabase
            .from('profiles')
            .update({ 
              onboarding_completed: false,
              last_onboarding_step: 0
            })
            .eq('id', userId);
          
          if (onboardingError) throw onboardingError;
          toast({
            title: "Onboarding resetado",
            description: "Usuário precisará completar onboarding novamente",
          });
          break;

        case 'suspend':
          const { error: suspendError } = await supabase
            .from('profiles')
            .update({ hidden: true })
            .eq('id', userId);
          
          if (suspendError) throw suspendError;
          toast({
            title: "Usuário suspenso",
            description: "Perfil oculto com sucesso",
          });
          break;

        default:
          debugLogger.debug('AdminUserManagement', `Action ${action} for user ${userId}`);
      }
      
      await refetch();
    } catch (error) {
      debugLogger.error('AdminUserManagement', `Error performing action ${action}`, error);
      toast({
        title: "Erro",
        description: `Falha ao executar ação: ${action}`,
        variant: "destructive",
      });
    }
  };

  const handleResetAccount = (user: any) => {
    setUserToReset(user);
    setResetDialogOpen(true);
  };

  const confirmResetAccount = async () => {
    if (!userToReset) return;
    
    try {
      const { error } = await supabase.rpc('reset_user_account', {
        target_user_id: userToReset.id
      });

      if (error) throw error;
      
      toast({
        title: "Conta resetada",
        description: "Todos os dados do usuário foram removidos",
      });
      
      setResetDialogOpen(false);
      setUserToReset(null);
      await refetch();
    } catch (error) {
      debugLogger.error('AdminUserManagement', 'Error resetting account', error);
      toast({
        title: "Erro",
        description: "Falha ao resetar conta",
        variant: "destructive",
      });
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      const { data, error } = await supabase.functions.invoke('delete-user-permanently', {
        body: { userId: userToDelete.id }
      });
      
      if (error) {
        // Handle specific error cases with informative messages
        if (error.message?.includes('403') || error.message?.includes('Apenas administradores')) {
          throw new Error('Você não tem permissão para deletar usuários. Apenas administradores podem executar esta ação.');
        }
        if (error.message?.includes('não pode deletar sua própria conta')) {
          throw new Error('Você não pode deletar sua própria conta de administrador.');
        }
        if (error.message?.includes('outros administradores')) {
          throw new Error('Não é possível deletar outros administradores.');
        }
        throw error;
      }
      
      debugLogger.info('AdminUserManagement', 'User deleted successfully', { 
        deletedUser: userToDelete.username,
        summary: data?.summary 
      });
      
      showSuccessToast({
        title: "✅ Usuário deletado permanentemente",
        description: `Conta de ${userToDelete.username} foi completamente removida`,
      });
      
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await refetch();
    } catch (error: any) {
      debugLogger.warn('AdminUserManagement', 'Failed to delete user', { 
        error: error.message,
        user: userToDelete.username 
      });
      
      showErrorToast({
        title: "⚠️ Não foi possível deletar",
        description: error.message || "Falha ao deletar usuário permanentemente",
      });
    }
  };

  const handleSendNotification = async (userId: string, title: string, message: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title,
          message,
          notification_type: 'admin_notification',
          category: 'admin'
        });

      if (error) throw error;
      
      toast({
        title: "Notificação enviada",
        description: "Usuário receberá a notificação",
      });
    } catch (error) {
      debugLogger.error('AdminUserManagement', 'Error sending notification', error);
      toast({
        title: "Erro",
        description: "Falha ao enviar notificação",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestão de Usuários</h2>
          <p className="text-white/60 text-sm">
            {users.length} de {totalUsers ?? users.length} usuários
          </p>
        </div>
        <Button 
          onClick={refetch} 
          variant="outline" 
          size="sm"
          className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Search and Filters */}
      <SearchAndFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortFieldChange={setSortField}
        onSortDirectionToggle={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
        planTypeFilter={planTypeFilter}
        onPlanTypeFilterChange={setPlanTypeFilter}
        filteredUsersCount={filteredUsers.length}
        filterOpen={filterOpen}
        onFilterOpenChange={setFilterOpen}
        activeFiltersCount={getActiveFiltersCount()}
      />

      {/* Users List */}
        <UsersList 
          users={sortedUsers}
          currentUserIsAdmin={currentUserIsAdmin}
          onViewProfile={handleViewProfile}
          onUserAction={handleUserAction}
          onResetAccount={handleResetAccount}
          onManageRole={handleManageRole}
          loadMoreUsers={loadMoreUsers}
          hasMoreUsers={hasMoreUsers}
          loadingMore={loadingMore}
        />

      {/* Reset Account Dialog */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resetar Conta do Usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. Todos os dados do usuário <strong>{userToReset?.username}</strong> serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmResetAccount} className="bg-destructive">
              Confirmar Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Permanently Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-black border-red-500/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              ⚠️ ATENÇÃO: Ação Irreversível
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/80 space-y-3">
              <p className="font-bold text-red-400">
                Você está prestes a DELETAR PERMANENTEMENTE a conta de:
              </p>
              <p className="text-lg font-bold text-white">
                {userToDelete?.username} ({userToDelete?.email})
              </p>
              
              <div className="bg-red-500/10 border border-red-500/30 rounded p-3 space-y-2">
                <p className="font-semibold text-red-400">Esta ação irá:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>❌ Deletar conta do Supabase Auth</li>
                  <li>🗑️ Remover TODAS as fotos do perfil</li>
                  <li>💬 Deletar TODAS as conversas e mensagens</li>
                  <li>🔗 Remover TODAS as conexões</li>
                  <li>📄 Apagar documentos de verificação</li>
                  <li>📊 Limpar logs de auditoria</li>
                  <li>🎫 Deletar tickets de suporte</li>
                </ul>
              </div>
              
              <p className="font-bold text-red-500 text-center py-2">
                ⚠️ ESTA AÇÃO NÃO PODE SER DESFEITA ⚠️
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white hover:bg-white/20">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteUser}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              Sim, Deletar Permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Actions Modal */}
      <UserActionsModal
        isOpen={actionsModalOpen}
        onClose={() => setActionsModalOpen(false)}
        user={selectedUser}
        onSendNotification={handleSendNotification}
        onManagePhotos={() => {}}
        onForceOnboarding={() => {}}
      />


      {/* Admin Filters Modal */}
      <AdminFiltersModal
        open={filterOpen}
        onOpenChange={setFilterOpen}
        filters={filters}
        onUpdateFilter={updateFilter}
        onClearFilters={clearFilters}
      />

      {/* Role Management Dialog */}
      <RoleManagementDialog
        isOpen={roleModalOpen}
        onClose={() => setRoleModalOpen(false)}
        user={selectedUser}
        currentUserId={currentUserId}
        onRoleChanged={refetch}
      />
    </div>
  );
};

export default AdminUserManagement;
